import { useMutation } from "@apollo/client";
import { Alert, Button, Stack, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { StateFieldsForm } from "components/Forms/StateFieldsForm";
import { CatalogLink } from "components/ModelLinks/CatalogLink";
import { FacilityChemicalLink } from "components/ModelLinks/FacilityChemicalLink";
import { ProductInventoryLink } from "components/ModelLinks/ProductInventoryLink";
import { SkeletonFormGroup } from "components/Skeleton";
import { ThresholdChip } from "components/ThresholdChip";
import { gql } from "generated-graphql";
import {
  DynamicField,
  IssueLevel,
  ProgramArea,
  ReportingFacilityChemicalQuery,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformStateField";
import { useMeasurements } from "hooks/useMeasurements";
import { useValidatingForm } from "hooks/useValidatingForm";
import { omit } from "lodash-es";
import { useCallback, useMemo } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useParams } from "react-router-dom";
import { anySegment } from "util/issues";
import { GET_REPORT } from "../Report/useReport";
import { useReportIsVerified } from "../Report/useReportIsVerified";
import { useReportingFacilityChemicalInputValidation } from "../Report/validationHooks/useReportingFacilityChemicalInputValidation";
import { MeasurementsTable } from "./MeasurementsTable";
import { ProductsTable } from "./ProductsTable";
import { REPORTING_FACILITY_CHEMICAL_QUERY } from "./ReportingFacilityChemicalEditor";
import { REPORTING_FACILITY_CHEMICALS_QUERY } from "./ReportingFacilityChemicalTable";
import { StorageLocationsTable } from "./StorageLocationsTable";
import { SummaryCard } from "./SummaryCard";

type FormValues = {
  isAlwaysReported?: boolean | null;
  stateFields: (DynamicField & { type: ProgramArea })[];
};

const UPSERT_FACILITY_CHEMICAL_STATE_FIELDS = gql(`
  mutation UpsertFacilityChemicalStateFields($input: [FacilityChemicalStateFieldInput!]!, $facilityId: ID!, $chemicalId: ID!, $reportingYear: Int!) {
    upsertFacilityChemicalStateFields(input: $input, facilityId: $facilityId, chemicalId: $chemicalId, reportingYear: $reportingYear, type: EPCRA) {
      id
    }
  }
`);

const SET_IS_ALWAYS_REPORTED = gql(`
  mutation SetIsAlwaysReportedOnRfc($id: ID!, $isAlwaysReported: Boolean) {
    setIsAlwaysReportedOnReportingFacilityChemical(id: $id, isAlwaysReported: $isAlwaysReported) {
      id
      isAlwaysReported
    }
  }
`);

export const ReportingFacilityChemicalDialog = ({
  rfc,
  loading: loadingRfc,
  onClose,
}: {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"] | undefined;
  loading: boolean;
  onClose: () => void;
}) => {
  const isVerified = useReportIsVerified();
  const theme = useTheme();
  const [upsertStateFields, { loading: savingStateFields }] = useMutation(
    UPSERT_FACILITY_CHEMICAL_STATE_FIELDS
  );
  const [setIsAlwaysReported, { loading: savingIsAlwaysReported }] =
    useMutation(SET_IS_ALWAYS_REPORTED);

  const { tenantId, reportId } = useParams();

  const saving = savingStateFields || savingIsAlwaysReported;

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      if (!rfc) return;

      await setIsAlwaysReported({
        variables: { id: rfc.id, isAlwaysReported: data.isAlwaysReported },
      });

      await upsertStateFields({
        variables: {
          input: data.stateFields.map((stateField) => {
            return {
              ...transform(stateField),
              facilityId: rfc.facilityId,
              chemicalId: rfc.chemicalId,
              reportingYear: rfc.reportingYear,
            };
          }),
          facilityId: rfc.facilityId,
          chemicalId: rfc.chemicalId,
          reportingYear: rfc.reportingYear,
        },
        onCompleted() {
          onClose();
        },
        refetchQueries: [
          REPORTING_FACILITY_CHEMICAL_QUERY,
          REPORTING_FACILITY_CHEMICALS_QUERY,
          GET_REPORT,
        ],
      });
    },
    [onClose, rfc, setIsAlwaysReported, upsertStateFields]
  );

  const defaultValues = useMemo<FormValues>(
    () => ({
      isAlwaysReported: rfc?.isAlwaysReported ?? false,
      stateFields:
        rfc?.stateFields.map((sf) => ({
          ...omit(sf, "selectOptions", "tooltip"),
          value: sf.value ?? null,
          type: ProgramArea.Epcra,
        })) ?? [],
    }),
    [rfc]
  );

  const { hasMeasurements } = useMeasurements(rfc?.facilityId ?? "");

  const uniqueProducts = useMemo(() => {
    if (!rfc) return [];

    if (hasMeasurements) {
      // Measurement mode: use catalog product IDs from measurements
      const products = (rfc.origin?.facilityProductMeasurements ?? [])
        .filter(
          (
            fpm
          ): fpm is typeof fpm & { product: { id: string; name: string } } =>
            !!fpm?.product?.id && !!fpm?.product?.name
        )
        .map((fpm) => ({
          catalogProductId: fpm.product.id,
          inventoryLinkId: fpm.product.id, // Same as catalog in measurement mode
          name: fpm.product.name,
        }));

      const productsMap = new Map(products.map((p) => [p.catalogProductId, p]));
      return Array.from(productsMap.values());
    } else {
      // Non-measurement mode: use facility product IDs for inventory links
      const products = (rfc.origin?.facilityProducts ?? [])
        .filter(
          (fp): fp is typeof fp & { product: { id: string; name: string } } =>
            !!fp?.id && !!fp?.product?.id && !!fp?.product?.name
        )
        .map((fp) => ({
          catalogProductId: fp.product.id,
          inventoryLinkId: fp.id, // Facility product ID for inventory link
          name: fp.product.name,
        }));

      const productsMap = new Map(products.map((p) => [p.catalogProductId, p]));
      return Array.from(productsMap.values());
    }
  }, [rfc, hasMeasurements]);

  const hasChemicalOrigin = useMemo(
    () =>
      !!rfc?.origin?.facilityChemicalId ||
      (rfc?.origin?.facilityChemicalMeasurements?.length ?? 0) > 0,
    [
      rfc?.origin?.facilityChemicalId,
      rfc?.origin?.facilityChemicalMeasurements?.length,
    ]
  );

  const form = useValidatingForm<FormValues>(
    defaultValues,
    rfc?.issues.filter(
      (issue) =>
        !anySegment(issue, (s) => s.model === "ReportingFacilityChemicalOrigin")
    ),
    useReportingFacilityChemicalInputValidation(rfc),
    undefined,
    {
      isDisplayOnly: isVerified,
    }
  );

  const renderIssueListButton = () => {
    if (rfc?.aggregationErrorMessage) {
      return (
        <IssueListButton
          issues={[
            {
              message: rfc.aggregationErrorMessage,
              level: IssueLevel.Critical,
              modelName: "ReportingFacilityChemical",
            },
          ]}
        />
      );
    }

    if (form.issues) {
      return (
        <IssueListButton
          issues={form.issues.filter(
            (issue) =>
              !anySegment(
                issue,
                (s) => s.model === "ReportingFacilityChemicalOrigin"
              )
          )}
        />
      );
    }

    return null;
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      title={
        rfc ? (
          <div>
            {`${rfc.reportingYear} Reporting Summary for ${rfc.chemical?.name}`}
            <ThresholdChip
              isOverThreshold={rfc.isOverThreshold ?? false}
              sx={{ ml: 1 }}
            />
          </div>
        ) : (
          "Loading..."
        )
      }
      noPrimaryAction={isVerified ?? false}
      additionalPrimaryActionElements={
        isVerified
          ? [
              <Button key="done-button" onClick={onClose}>
                Done
              </Button>,
            ]
          : [renderIssueListButton()]
      }
      primaryActionButtonProps={{
        loading: saving,
        disabled: loadingRfc,
        onClick: form.handleSubmit(onSubmit),
      }}
    >
      {!rfc && <SkeletonFormGroup count={8} />}
      {rfc && (
        <Stack spacing={4}>
          {!rfc.isOverThreshold && (
            <CheckboxField
              label="Voluntarily report this chemical"
              name="isAlwaysReported"
              disabled={isVerified}
              control={form.control}
              noErrorDisplay
            />
          )}

          {rfc.aggregationErrorMessage && (
            <Alert severity="error">{rfc.aggregationErrorMessage}</Alert>
          )}
          <SummaryCard rfc={rfc} />

          {rfc.stateFields.length > 0 && (
            <FormProvider {...form}>
              <StateFieldsForm
                namePrefix="stateFields"
                fields={rfc.stateFields}
                context="facilityChemical"
                sx={{ p: theme.spacing(3) }}
              />
            </FormProvider>
          )}

          <MeasurementsTable rfc={rfc} />

          <ProductsTable rfc={rfc} />

          <StorageLocationsTable rfc={rfc} />
          <CatalogLink
            id={rfc.chemical?.id ?? ""}
            name={rfc.chemical?.name ?? ""}
            type="Chemical"
            issues={
              isVerified
                ? []
                : rfc.issues.filter(
                    (issue) =>
                      !anySegment(
                        issue,
                        (s) => s.model === "ReportingFacilityChemicalOrigin"
                      )
                  )
            }
          />
          {hasChemicalOrigin && (
            <FacilityChemicalLink
              facilityChemicalId={rfc.origin?.facilityChemicalId ?? ""}
              chemicalId={rfc.chemical?.id ?? ""}
              reportId={reportId}
              facilityId={rfc.facilityId}
              tenantId={tenantId ?? ""}
              name={rfc.chemical?.name ?? ""}
              reportIsVerified={isVerified}
              reportingYear={rfc.reportingYear}
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter((issue) =>
                      anySegment(
                        issue,
                        (s) => s.model === "ReportingFacilityChemicalOrigin"
                      )
                    )
              }
            />
          )}
          {uniqueProducts.map(({ catalogProductId, name }) => (
            <CatalogLink
              key={`catalog-${catalogProductId}`}
              id={catalogProductId}
              name={name}
              type="Product"
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter(
                      (issue) =>
                        !anySegment(
                          issue,
                          (s) => s.model === "ReportingFacilityChemicalOrigin"
                        )
                    )
              }
            />
          ))}
          {uniqueProducts.map(({ inventoryLinkId, name }) => (
            <ProductInventoryLink
              key={`inventory-${inventoryLinkId}`}
              tenantId={tenantId ?? ""}
              reportId={reportId}
              facilityId={rfc.facilityId}
              productId={inventoryLinkId}
              productName={name}
              reportingYear={rfc.reportingYear}
              issues={
                isVerified
                  ? []
                  : rfc.issues.filter(
                      (issue) =>
                        !anySegment(
                          issue,
                          (s) => s.model === "ReportingFacilityChemicalOrigin"
                        )
                    )
              }
            />
          ))}
        </Stack>
      )}
    </Dialog>
  );
};
